import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import { TypedMemo } from '../../../common/types';
import { AUTOSAVE_DELAY_MS } from '../forms/useBlockAutosave';
import { useEditorContext } from '../SMLEditorContext';
import { useEditorSaves } from '../saves/useEditorSaves';
import { pageMessages as m } from './pageMessages';
import { useSavePageFields } from './useSavePageFields';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for several parts; each part uses its own slice of it */
import styles from './SMLPageTab.module.scss';
/* eslint-enable css-modules/no-unused-class */

/** The name of the page (design a6). It saves to the draft 600 ms after the last key. */
const SMLPageNameCardComponent: React.FC = () => {
  const intl = useIntl();
  const { smartLink } = useEditorContext();
  const saves = useEditorSaves();
  const saverKey = `page-name-${useId()}`;
  const savedName = smartLink.recentSmartLinkVersion.name;
  const [name, setName] = useState(savedName);
  const { state, save } = useSavePageFields(smartLink);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isEmpty = name.trim() === '';

  const stopTimer = useCallback(() => {
    if (timer.current !== null) {
      clearTimeout(timer.current);
      timer.current = null;
    }
    saves?.unregister(saverKey);
  }, [saverKey, saves]);

  // The editor closes: a name that waits is sent now, as a block form does.
  const flushRef = useRef<() => void>(() => undefined);
  useEffect(() => () => flushRef.current(), []);

  const onName = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const next = event.target.value;
      setName(next);
      stopTimer();
      if (next.trim() === '' || next === savedName) {
        flushRef.current = () => undefined;
        return;
      }
      const send = () => {
        stopTimer();
        flushRef.current = () => undefined;
        save({ name: next }).catch(console.error);
      };
      flushRef.current = send;
      timer.current = setTimeout(send, AUTOSAVE_DELAY_MS);
      // Publish sends the name at once instead of waiting for the delay.
      saves?.register(saverKey, send);
    },
    [save, savedName, saverKey, saves, stopTimer]
  );

  let stateText: string | null = null;
  if (state === 'saving') {
    stateText = intl.formatMessage(m.saving);
  } else if (state === 'saved') {
    stateText = intl.formatMessage(m.saved);
  } else if (state === 'failed') {
    stateText = intl.formatMessage(m.saveFailed);
  }

  return (
    <section className={styles.card}>
      <h4 className={styles.cardTitle}>
        {intl.formatMessage(m.name)}
        {stateText !== null && <span className={styles.saveState}>{stateText}</span>}
      </h4>
      <input
        className={clsx(styles.input, styles.inputBox, isEmpty && styles.inputBad)}
        value={name}
        onChange={onName}
        aria-label={intl.formatMessage(m.name)}
        data-testid="page-name"
      />
      <p className={isEmpty ? styles.helpBad : styles.help}>{intl.formatMessage(isEmpty ? m.nameEmpty : m.nameHelp)}</p>
    </section>
  );
};

export const SMLPageNameCard = TypedMemo(SMLPageNameCardComponent);
